var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// client/extensions/ContainerSectionExtension.ts
import { selectionSet } from "@adornis/baseql/utils/queryGeneration.js";
import { RXController } from "@adornis/chemistry/controllers/RXController.js";
import { css } from "@adornis/chemistry/directives/css.js";
import "@adornis/chemistry/elements/components/x-section.js";
import { DesignSystem } from "@adornis/chemistry/elements/theming/design.js";
import { xComponents } from "@adornis/chemistry/elements/x-components.js";
import { html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { distinctUntilChanged, filter, switchMap, takeUntil } from "rxjs";
import { BaseContainerSection } from "../../db/extensions/BaseSection.js";
import { BuildifyGlobalSettings, subscribeBuildifyGlobalSettings } from "../../db/GlobalSettings.js";
import "../components/x-buildify-width-picker.js";
import { GlobalSettingsClassNameConsumer } from "../globals/consumer.js";
import { XContainerExtension } from "./ContainerExtension.js";
import { ContainerFlexExtension } from "./ContainerFlexExtension.js";
var ContainerSectionExtension = {
  class: BaseContainerSection,
  toolbar: { icon: "view_carousel", text: { default: "Section" } },
  render(instance, { defaultValues, isSubelement, dropzoneBefore, dropzoneAfter }) {
    return html`
      <x-section-extension
        .content=${instance}
        .defaultValues=${defaultValues}
        ?sub=${isSubelement}
        .hasDropzoneBefore=${dropzoneBefore}
        .hasDropzoneAfter=${dropzoneAfter}
      ></x-section-extension>
    `;
  },
  showDefaultEditor: false,
  editor({ content, contentController, controllerBaseKeyPath, host }) {
    return html`
      <x-flex space="sm">
        <x-buildify-width-picker
          clearable
          placeholder="Inhaltsbreite"
          ${contentController.field(...controllerBaseKeyPath, "contentWidthID")}
        ></x-buildify-width-picker>
        ${ContainerFlexExtension.editor({ content, contentController, controllerBaseKeyPath, host })}
      </x-flex>
    `;
  },
  group: "Layout"
};
var XSectionExtension = class extends XContainerExtension {
  constructor() {
    super(...arguments);
    this._consumedGlobalSettingsClassName = GlobalSettingsClassNameConsumer(this);
    this._globalSettingsClassName = new RXController(this, void 0);
    this._globalSettings = new RXController(
      this,
      this._globalSettingsClassName.observable.pipe(
        filter(Boolean),
        distinctUntilChanged(),
        switchMap(
          (className) => subscribeBuildifyGlobalSettings(className)(selectionSet(() => BuildifyGlobalSettings, 10))
        ),
        takeUntil(this.disconnected)
      )
    );
  }
  willUpdate(_changedProperties) {
    super.willUpdate(_changedProperties);
    if (this._consumedGlobalSettingsClassName.value !== this._globalSettingsClassName.value)
      this._globalSettingsClassName.next(this._consumedGlobalSettingsClassName.value);
  }
  _content(content) {
    if (this._contentFilteredSorted?.length === 0) {
      return html` <x-buildify-dropzone .index=${0} .parentID=${content._id}></x-buildify-dropzone> `;
    }
    const contents = repeat(
      this._contentFilteredSorted ?? [],
      (content2, index) => this.__item(content2, content2.index, index)
    );
    return xComponents(
      html`
        <x-section
          ${css({
        ...this._getMaxWidthCSS(content),
        flexDirection: this.defaultValue("flexDirection", { global: "column" }),
        rowGap: this.getValue("rowGap"),
        columnGap: this.getValue("columnGap"),
        justifyContent: this.getValue("justifyContent"),
        alignItems: this.getValue("alignItems"),
        padding: "0"
      })}
        >
          ${contents}
        </x-section>
      `
    );
  }
  _getMaxWidthCSS(content) {
    if (this._globalSettings.value?.widths && content.contentWidthID && this._globalSettings.value?.widths.find((elm) => elm._id === content.contentWidthID)?.spacing) {
      const width = this._globalSettings.value?.widths.find((elm) => elm._id === content.contentWidthID);
      return { maxWidth: width.spacing };
    }
    if (this._globalSettings.value?.sectionWidth)
      return { maxWidth: this._globalSettings.value.sectionWidth };
    return {};
  }
  styles() {
    return [
      ...super.styles(),
      {
        [`${DesignSystem.prefix}-section`]: {
          padding: "0"
        }
      }
    ];
  }
};
__decorateClass([
  state()
], XSectionExtension.prototype, "_globalSettings", 2);
XSectionExtension = __decorateClass([
  customElement("x-section-extension")
], XSectionExtension);
export {
  ContainerSectionExtension,
  XSectionExtension
};
